"use client";

import Link from "next/link";
import { FormEvent, useEffect, useRef, useState } from "react";
import { company } from "../contact/details";
import { CONTACT_TOPICS, type ContactField, validateContactFields } from "../contact/validation";

type SubmissionState = "idle" | "submitting" | "success" | "error";

type ContactResponse = {
  error?: string;
  fields?: Partial<Record<ContactField, string>>;
};

type FieldErrors = Partial<Record<ContactField, string>>;

const fieldOrder: ContactField[] = ["name", "email", "phone", "organization", "topic", "message"];

function readFields(form: HTMLFormElement) {
  const data = new FormData(form);
  return Object.fromEntries(
    fieldOrder.map((field) => [field, String(data.get(field) ?? "")]),
  ) as Record<ContactField, string>;
}

export function ContactForm() {
  const [state, setState] = useState<SubmissionState>("idle");
  const [message, setMessage] = useState("");
  const [errors, setErrors] = useState<FieldErrors>({});
  const startedAt = useRef<number | null>(null);
  const formRef = useRef<HTMLFormElement>(null);
  const successRef = useRef<HTMLHeadingElement>(null);

  useEffect(() => {
    startedAt.current = Date.now();
  }, []);

  useEffect(() => {
    if (state === "success") successRef.current?.focus();
  }, [state]);

  function startAnother() {
    startedAt.current = Date.now();
    setState("idle");
    setMessage("");
  }

  function focusFirstInvalid(fieldErrors: FieldErrors) {
    const first = fieldOrder.find((field) => fieldErrors[field]);
    if (first) formRef.current?.querySelector<HTMLElement>(`[name="${first}"]`)?.focus();
  }

  // Re-check a field once the visitor leaves it, but only after it has already been flagged.
  function revalidate(field: ContactField) {
    if (!errors[field] || !formRef.current) return;
    const next = validateContactFields(readFields(formRef.current));
    setErrors((current) => ({ ...current, [field]: next[field] }));
  }

  async function submit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const form = event.currentTarget;
    const fields = readFields(form);
    const fieldErrors = validateContactFields(fields);
    const errorCount = Object.values(fieldErrors).filter(Boolean).length;

    if (errorCount) {
      setErrors(fieldErrors);
      setState("error");
      setMessage(
        errorCount === 1
          ? "יש לתקן שדה אחד לפני השליחה."
          : `יש לתקן ${errorCount} שדות לפני השליחה.`,
      );
      focusFirstInvalid(fieldErrors);
      return;
    }

    setErrors({});
    setState("submitting");
    setMessage("");

    try {
      const response = await fetch("/api/contact", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          ...fields,
          website: String(new FormData(form).get("website") ?? ""),
          startedAt: startedAt.current ?? Date.now(),
        }),
      });
      const body = (await response.json().catch(() => ({}))) as ContactResponse;

      if (!response.ok) {
        setState("error");
        setMessage(body.error ?? "לא הצלחנו לשלוח את הפנייה. נסו שוב בעוד כמה דקות.");
        if (body.fields) {
          setErrors(body.fields);
          focusFirstInvalid(body.fields);
        }
        return;
      }

      form.reset();
      setState("success");
      setMessage("");
    } catch {
      setState("error");
      setMessage("אין חיבור לשרת כרגע. בדקו את החיבור לאינטרנט ונסו שוב. הפרטים שמילאתם נשמרו.");
    }
  }

  const disabled = state === "submitting";
  const describedBy = (field: ContactField, hint?: string) =>
    [hint, errors[field] ? `${field}-error` : null].filter(Boolean).join(" ") || undefined;
  const fieldError = (field: ContactField) =>
    errors[field] ? (
      <p className="field-error" id={`${field}-error`}>
        {errors[field]}
      </p>
    ) : null;

  if (state === "success") {
    return (
      <div className="contact-form contact-success">
        <h2 ref={successRef} tabIndex={-1}>
          תודה, הפנייה התקבלה.
        </h2>
        <p>
          נחזור אליכם בהקדם כדי להבין את הצורך ולהכין הצעת מחיר מותאמת. אם הנושא דחוף, אפשר להתקשר
          אלינו:{" "}
          <a href={company.phoneHref} dir="ltr">
            {company.phoneDisplay}
          </a>
          .
        </p>
        <button className="button button-quiet" type="button" onClick={startAnother}>
          שליחת פנייה נוספת
        </button>
      </div>
    );
  }

  return (
    <form
      ref={formRef}
      className="contact-form"
      noValidate
      onSubmit={submit}
      aria-labelledby="form-title"
      aria-describedby="form-intro"
    >
      <div className="form-head">
        <h2 id="form-title">פרטי הפנייה</h2>
        <p className="form-intro" id="form-intro">
          כל השדות הם חובה, אלא אם צוין אחרת.
        </p>
        <p
          className={state === "error" ? "form-error" : "form-status"}
          id="form-status"
          role={state === "error" ? "alert" : "status"}
          aria-live={state === "error" ? "assertive" : "polite"}
        >
          {message}
        </p>
      </div>
      <div className="form-row">
        <div className="form-field">
          <label htmlFor="contact-name">שם מלא</label>
          <input
            id="contact-name"
            required
            name="name"
            autoComplete="name"
            maxLength={120}
            disabled={disabled}
            aria-invalid={errors.name ? true : undefined}
            aria-describedby={describedBy("name")}
            onBlur={() => revalidate("name")}
          />
          {fieldError("name")}
        </div>
        <div className="form-field">
          <label htmlFor="contact-email">אימייל</label>
          <input
            id="contact-email"
            required
            name="email"
            type="email"
            dir="ltr"
            autoComplete="email"
            inputMode="email"
            maxLength={254}
            disabled={disabled}
            aria-invalid={errors.email ? true : undefined}
            aria-describedby={describedBy("email")}
            onBlur={() => revalidate("email")}
          />
          {fieldError("email")}
        </div>
      </div>
      <div className="form-row">
        <div className="form-field">
          <label htmlFor="contact-phone">
            טלפון <span className="field-optional">(לא חובה)</span>
          </label>
          <input
            id="contact-phone"
            name="phone"
            type="tel"
            dir="ltr"
            autoComplete="tel"
            inputMode="tel"
            maxLength={30}
            disabled={disabled}
            aria-invalid={errors.phone ? true : undefined}
            aria-describedby={describedBy("phone")}
            onBlur={() => revalidate("phone")}
          />
          {fieldError("phone")}
        </div>
        <div className="form-field">
          <label htmlFor="contact-organization">
            ארגון <span className="field-optional">(לא חובה)</span>
          </label>
          <input
            id="contact-organization"
            name="organization"
            autoComplete="organization"
            maxLength={160}
            disabled={disabled}
          />
        </div>
      </div>
      <div className="form-field">
        <label htmlFor="contact-topic">
          נושא הפנייה <span className="field-optional">(לא חובה)</span>
        </label>
        <select
          id="contact-topic"
          name="topic"
          defaultValue={CONTACT_TOPICS[0]}
          disabled={disabled}
          aria-invalid={errors.topic ? true : undefined}
          aria-describedby={describedBy("topic")}
        >
          <option value="">בחרו נושא</option>
          {CONTACT_TOPICS.map((topic) => (
            <option key={topic} value={topic}>
              {topic}
            </option>
          ))}
        </select>
        {fieldError("topic")}
      </div>
      <div className="form-field">
        <label htmlFor="contact-message">במה נוכל לעזור?</label>
        <p className="field-hint" id="message-hint">
          למשל: איזה ציוד או אילו תנאי סביבה תרצו לנטר, היכן ובאיזה היקף.
        </p>
        <textarea
          id="contact-message"
          required
          name="message"
          rows={5}
          maxLength={3000}
          disabled={disabled}
          aria-invalid={errors.message ? true : undefined}
          aria-describedby={describedBy("message", "message-hint")}
          onBlur={() => revalidate("message")}
        />
        {fieldError("message")}
      </div>
      <div className="honeypot" aria-hidden="true">
        <label htmlFor="contact-website">אתר</label>
        <input id="contact-website" name="website" tabIndex={-1} autoComplete="off" />
      </div>
      <p className="form-privacy">
        הפרטים משמשים לטיפול בפנייה בלבד. <Link href="/privacy">מדיניות הפרטיות</Link>
      </p>
      <button className="button" type="submit" disabled={disabled}>
        {disabled ? "שולחים…" : "שליחת פנייה"}
      </button>
    </form>
  );
}
